import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormField,
  Header,
  Input,
  Modal,
  Select,
  SpaceBetween,
  Table,
  Tabs,
} from "@amzn/awsui-components-react/polaris";
import { useSelector } from "react-redux";

import CustomMultiHybridInputAndSelect from "../Templates/components/CustomMultiHybridInputAndSelect";
import { useSuggestedLogins } from "src/hooks/useSuggestedLogins";
// Email Mapping is backed by the SAME datasource + APIs as the SEC Central admin
// panel's "Non-Amazon aliases" section (table acc_central.non_amazon_aliases):
// global (not per-dashboard) mappings, an alias can hold multiple delivery
// emails (one row per (alias, email)), add/delete inline.
import {
  getNonAmazonAliases,
  addNonAmazonAlias,
  deleteNonAmazonAlias,
} from "src/utilities/AppSyncPlusAPIs";

const CADENCE_OPTIONS = [
  { label: "Monthly", value: "MONTHLY" },
  { label: "Quarterly", value: "QUARTERLY" },
  { label: "Annually", value: "ANNUALLY" },
  { label: "Workday", value: "WORKDAY" },
];

// Save persists only the active tab, so the Save button names the tab it will
// write to make that explicit (keys match the Tabs component's tab ids).
const TAB_LABELS = {
  controls: "Controls",
  mapping: "Mapping Configuration",
  email: "Email Mapping",
};

// Status options for the Mapping Configuration tab.
const STATUS_OPTIONS = [
  { label: "Success", value: "SUCCESS" },
  { label: "Error", value: "ERROR" },
  { label: "Warning", value: "WARNING" },
];

// Principal type options. "USER" maps to the value the shared component treats
// as an alias-autosuggest field ("UPLOADER"); "LDAP" is a plain text input.
// This mirrors how Frisco uploaders/approvers are entered across the app.
const PRINCIPAL_TYPE_OPTIONS = [
  { label: "USER", value: "UPLOADER" },
  { label: "LDAP", value: "UPLOADER_LDAP" },
];

/**
 * Dashboard "Control Panel" settings dialog (admin only).
 *
 * Owner/Viewer entry reuses the shared CustomMultiHybridInputAndSelect + the
 * useSuggestedLogins alias-fetch hook, so USER rows get the same phonetool alias
 * autosuggest used everywhere else and LDAP rows get a plain text input.
 */
const ControlPanelModal = ({
  visible,
  onDismiss,
  onSave,
  dashboard,
  loading = false,
  saving = false,
  error = "",
}) => {
  // Stable ids for the dynamically-added mapping rows. Keying these rows by the
  // array index makes controlled <Input>/<Select>s keep a sibling's focus/cursor
  // when a middle row is removed, so each row carries its own id instead.
  const nextId = useRef(0);
  const makeStatusRow = (row = {}) => ({
    id: nextId.current++,
    key: row.key ?? "",
    value: row.value ?? "",
    status: row.status ?? null,
  });
  const makeEmailRow = (row = {}) => ({
    id: nextId.current++,
    alias: row.alias ?? "",
    emails: row.emails?.length ? row.emails : [""],
  });

  const userId = useSelector((globalState) => globalState.auth.userId);

  const [dailyDigest, setDailyDigest] = useState(false);
  const [notifyDuplicate, setNotifyDuplicate] = useState(false);
  const [cadence, setCadence] = useState(null);
  const [day, setDay] = useState("");
  const [activeTabId, setActiveTabId] = useState("controls");

  // owners/viewers are arrays of { type, alias } managed by the shared component.
  const [perms, setPerms] = useState({ owners: [], viewers: [] });

  // Mapping Configuration: [{ id, key, value, status }]
  const [statusMappings, setStatusMappings] = useState(() => [makeStatusRow()]);

  // Email Mapping (Non-Amazon aliases): loaded/added/deleted directly against
  // the shared getNonAmazonAliases/addNonAmazonAlias/deleteNonAmazonAlias APIs,
  // independent of the per-tab onSave/preferences flow. Each item is
  // { id, alias, email, domain, created_by }.
  const [nonAmazonAliases, setNonAmazonAliases] = useState([]);
  const [aliasesLoader, setAliasesLoader] = useState(false);
  const [aliasesError, setAliasesError] = useState("");
  const [addAliasLoader, setAddAliasLoader] = useState(false);
  // Entry UI (old style): each card is one alias with one or more delivery
  // emails: [{ id, alias, emails: [""] }]. On save we loop addNonAmazonAlias for
  // every (alias, email) pair - the backend keys by (alias, email), so an alias
  // can hold several emails and re-adding a pair is idempotent.
  const [emailMappings, setEmailMappings] = useState(() => [makeEmailRow()]);

  // Re-seed the form from the dashboard prop each time the modal opens. The
  // component stays mounted with `visible` toggling, so without this a user who
  // edits, cancels, and reopens would see their stale unsaved edits instead of
  // the persisted values.
  useEffect(() => {
    if (!visible) return;
    setDailyDigest(dashboard?.notifications?.dailyDigest ?? false);
    setNotifyDuplicate(dashboard?.notifications?.notifyDuplicate ?? false);
    const persistedCadence =
      dashboard?.cadence === "WORKDAY_BASED" ? "WORKDAY" : dashboard?.cadence;
    setCadence(
      persistedCadence
        ? CADENCE_OPTIONS.find((o) => o.value === persistedCadence) ?? null
        : null
    );
    setDay(persistedCadence === "WORKDAY" && dashboard?.day != null ? String(dashboard.day) : "");
    setPerms({
      owners: dashboard?.owners ?? [],
      viewers: dashboard?.viewers ?? [],
    });
    setStatusMappings(
      dashboard?.statusMappings?.length
        ? dashboard.statusMappings.map(makeStatusRow)
        : [makeStatusRow()]
    );
    // The persisted alias→email mappings are shown in the list below (loaded from
    // getNonAmazonAliases), so the entry cards start blank each open.
    setEmailMappings([makeEmailRow()]);
    // makeStatusRow/makeEmailRow are stable for the component's lifetime; only
    // re-seed when the modal opens or the dashboard changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, dashboard]);

  const { suggestedLogins, aliasesLoading, getSuggestedLoginsCaller } = useSuggestedLogins();

  const getValues = (field) => perms[field] || [];
  const updateValue = (field, values) => setPerms((prev) => ({ ...prev, [field]: values }));
  const getTypeOptions = () => PRINCIPAL_TYPE_OPTIONS;

  // ---- Status mapping helpers (keyed by row id) ----
  const updateMapping = (id, patch) =>
    setStatusMappings((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  const removeMapping = (id) =>
    setStatusMappings((prev) =>
      prev.length > 1 ? prev.filter((m) => m.id !== id) : [makeStatusRow()]
    );
  const addMapping = () =>
    setStatusMappings((prev) => [...prev, makeStatusRow()]);

  // ---- Email mapping (Non-Amazon aliases) — same APIs as SEC Central admin ----
  const loadNonAmazonAliases = () => {
    setAliasesLoader(true);
    setAliasesError("");
    getNonAmazonAliases(
      (result) => {
        let data = result?.data?.getNonAmazonAliases;
        // The op returns AWSJSON, which arrives as a JSON string.
        if (typeof data === "string") {
          data = JSON.parse(data);
        }
        setNonAmazonAliases(Array.isArray(data) ? data : []);
        setAliasesLoader(false);
      },
      () => {
        setAliasesLoader(false);
        setAliasesError("Could not load non-Amazon aliases");
      }
    );
  };

  // Load the (global) alias→email mappings when the modal opens on the Email tab.
  useEffect(() => {
    if (visible && activeTabId === "email") loadNonAmazonAliases();
    // loadNonAmazonAliases is stable; re-run only when the tab/modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, activeTabId]);

  // ---- Entry-card helpers (alias + multiple delivery emails), keyed by row id --
  const updateEmailAlias = (id, alias) =>
    setEmailMappings((prev) => prev.map((m) => (m.id === id ? { ...m, alias } : m)));
  const updateEmailAt = (id, emailIndex, value) =>
    setEmailMappings((prev) =>
      prev.map((m) =>
        m.id === id ? { ...m, emails: m.emails.map((e, j) => (j === emailIndex ? value : e)) } : m
      )
    );
  const addEmail = (id) =>
    setEmailMappings((prev) =>
      prev.map((m) => (m.id === id ? { ...m, emails: [...m.emails, ""] } : m))
    );
  const removeEmailCard = (id) =>
    setEmailMappings((prev) =>
      prev.length > 1 ? prev.filter((m) => m.id !== id) : [makeEmailRow()]
    );
  const addEmailCard = () => setEmailMappings((prev) => [...prev, makeEmailRow()]);

  // Promisified single add so we can loop over all (alias, email) pairs.
  //
  // The backend reports validation failures (unapproved domain, missing fields)
  // by resolving with a `{"error": "..."}` payload rather than raising - AppSync
  // returns HTTP 200, so `executeQuery`'s onSuccess callback fires and a plain
  // Promise wrapper would treat this as a successful add. Inspect the resolved
  // payload (which is an AWSJSON string) here and reject on `{error}` so callers
  // can distinguish real success from a silent validation failure.
  const addNonAmazonAliasAsync = (alias, email) =>
    new Promise((resolve, reject) =>
      addNonAmazonAlias(
        alias,
        email,
        userId,
        (result) => {
          let payload = result?.data?.addNonAmazonAlias;
          if (typeof payload === "string") {
            try {
              payload = JSON.parse(payload);
            } catch (e) {
              // Not JSON - accept it as a success sentinel.
            }
          }
          if (payload && typeof payload === "object" && payload.error) {
            reject(new Error(payload.error));
            return;
          }
          resolve(payload);
        },
        reject
      )
    );

  // Flatten the entry cards into (alias, email) pairs and add each via the shared
  // API (looping is fine - typically one email per alias). The list below is the
  // source of truth, so we reload it and reset the cards afterward.
  const submitEmailMappings = async () => {
    const pairs = [];
    emailMappings.forEach((card) => {
      const alias = (card.alias || "").trim();
      if (!alias) return;
      card.emails
        // A field may hold a single address or a comma-separated list.
        .flatMap((e) => e.split(","))
        .map((e) => e.trim())
        .filter(Boolean)
        .forEach((email) => pairs.push({ alias, email }));
    });
    if (pairs.length === 0) return;

    setAddAliasLoader(true);
    setAliasesError("");
    // Add each (alias, email) pair; the backend upserts on (alias, email), so
    // an alias accumulates all its emails and duplicates are idempotent. A single
    // failing pair (e.g. unapproved domain) must NOT hide the pairs that already
    // succeeded on this click, so reload the list either way and keep the modal
    // open on error so the admin can correct the offending row.
    const errors = [];
    for (const { alias, email } of pairs) {
      try {
        await addNonAmazonAliasAsync(alias, email);
      } catch (e) {
        errors.push(`${alias} → ${email}: ${e?.message || "add failed"}`);
      }
    }
    loadNonAmazonAliases();
    if (errors.length) {
      setAliasesError(
        errors.length === 1
          ? errors[0]
          : `Could not add ${errors.length} mapping(s):\n${errors.join("\n")}`
      );
    } else {
      setEmailMappings([makeEmailRow()]);
    }
    setAddAliasLoader(false);
  };

  const removeAlias = (alias, email) => {
    setAliasesLoader(true);
    setAliasesError("");
    // Delete just this one (alias, email) mapping. Like add, the backend surfaces
    // validation failures as a resolved `{error}` AWSJSON payload rather than
    // rejecting - inspect the response so an error shows up instead of silently
    // "succeeding" without a delete.
    deleteNonAmazonAlias(
      alias,
      email,
      (result) => {
        let payload = result?.data?.deleteNonAmazonAlias;
        if (typeof payload === "string") {
          try {
            payload = JSON.parse(payload);
          } catch (e) {
            // Not JSON - accept as success sentinel.
          }
        }
        if (payload && typeof payload === "object" && payload.error) {
          setAliasesLoader(false);
          setAliasesError(payload.error);
          return;
        }
        loadNonAmazonAliases();
      },
      () => {
        setAliasesLoader(false);
        setAliasesError("Could not delete alias");
      }
    );
  };

  const workdayDay = Number(day);
  const workdayDayInvalid =
    cadence?.value === "WORKDAY" &&
    (!Number.isInteger(workdayDay) || workdayDay < 1);

  const handleSave = () => {
    if (activeTabId === "controls") {
      if (workdayDayInvalid) return;
      onSave?.({
        activeTabId,
        notifications: { dailyDigest, notifyDuplicate },
        cadence: cadence?.value || null,
        day: cadence?.value === "WORKDAY" ? workdayDay : null,
        owners: (perms.owners || []).filter((o) => o.type && (o.alias || "").trim()),
        viewers: (perms.viewers || []).filter((v) => v.type && (v.alias || "").trim()),
      });
      return;
    }

    // The Email tab persists inline via the Non-Amazon alias APIs (add/delete),
    // so it has no batched Save; only controls/mapping route through onSave.
    if (activeTabId === "mapping") {
      onSave?.({
        activeTabId,
        statusMappings: statusMappings.filter(
          (m) => m.key.trim() && m.value.trim() && m.status
        ),
      });
    }
  };

  const principalList = (field) => (
    <CustomMultiHybridInputAndSelect
      field={field}
      selectKey="type"
      inputKey="alias"
      placeholder="alias"
      selectHint="Choose type"
      getValues={getValues}
      updateValue={updateValue}
      getOptions={getTypeOptions}
      duplicatesNotAllowed={true}
      info=" "
      errorMessage="Type and alias input are mandatory"
      loginOptions={suggestedLogins}
      getLoginOptions={getSuggestedLoginsCaller}
      aliasesLoading={aliasesLoading}
    />
  );

  const controlsTab = (
    <SpaceBetween size="l">
      <div>
        <Header variant="h3">Notifications</Header>
        <SpaceBetween size="xs">
          <Checkbox checked={dailyDigest} onChange={({ detail }) => setDailyDigest(detail.checked)}>
            Enable daily digest emails
          </Checkbox>
          <Checkbox
            checked={notifyDuplicate}
            onChange={({ detail }) => setNotifyDuplicate(detail.checked)}
          >
            Notify duplicate uploads
          </Checkbox>
        </SpaceBetween>
      </div>

      <div>
        <Header variant="h3">Cadence</Header>
        <SpaceBetween size="s">
          <FormField label="Frequency">
            <Select
              placeholder="Select cadence"
              selectedOption={cadence}
              options={CADENCE_OPTIONS}
              onChange={({ detail }) => {
                setCadence(detail.selectedOption);
                if (detail.selectedOption?.value !== "WORKDAY") setDay("");
              }}
            />
          </FormField>
          {cadence?.value === "WORKDAY" && (
            <FormField
              label="Day"
              description="Enter the workday number."
              errorText={
                cadence?.value === "WORKDAY" && day === ""
                  ? "Day is required."
                  : workdayDayInvalid
                  ? "Day must be a positive whole number."
                  : undefined
              }
            >
              <Input
                type="number"
                value={day}
                onChange={({ detail }) => setDay(detail.value)}
                placeholder="Enter day"
              />
            </FormField>
          )}
        </SpaceBetween>
      </div>

      <div>
        <Header variant="h3">Dashboard Owner</Header>
        <FormField description="Type and alias input are mandatory">{principalList("owners")}</FormField>
      </div>

      <div>
        <Header variant="h3">Viewer</Header>
        <FormField description="Type and alias input are mandatory">{principalList("viewers")}</FormField>
      </div>

      <div>
        <Header variant="h3">Download</Header>
        <SpaceBetween direction="horizontal" size="xs">
          <Button iconName="download">Download CSV</Button>
          <Button iconName="download">Download Excel</Button>
        </SpaceBetween>
      </div>
    </SpaceBetween>
  );

  const mappingTab = (
    <SpaceBetween size="l">
      <div>
        <Header variant="h3">Status Mapping Configuration</Header>
        <SpaceBetween size="xs">
          <div style={{ display: "flex", gap: 8, paddingRight: 32 }}>
            <div style={{ flex: 1 }}>
              <Box variant="awsui-key-label">Key</Box>
            </div>
            <div style={{ width: 20 }} />
            <div style={{ flex: 1 }}>
              <Box variant="awsui-key-label">Value</Box>
            </div>
            <div style={{ width: 200 }}>
              <Box variant="awsui-key-label">Status</Box>
            </div>
          </div>
          {statusMappings.map((row) => (
            <div key={row.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <Input
                  placeholder="Key"
                  value={row.key}
                  onChange={({ detail }) => updateMapping(row.id, { key: detail.value })}
                />
              </div>
              <span style={{ width: 12, textAlign: "center" }}>→</span>
              <div style={{ flex: 1 }}>
                <Input
                  placeholder="Value"
                  value={row.value}
                  onChange={({ detail }) => updateMapping(row.id, { value: detail.value })}
                />
              </div>
              <div style={{ width: 200 }}>
                <Select
                  placeholder="Status"
                  selectedOption={row.status ? STATUS_OPTIONS.find((o) => o.value === row.status) : null}
                  options={STATUS_OPTIONS}
                  onChange={({ detail }) => updateMapping(row.id, { status: detail.selectedOption.value })}
                />
              </div>
              <Button
                variant="icon"
                iconName="close"
                ariaLabel="Remove mapping"
                onClick={() => removeMapping(row.id)}
              />
            </div>
          ))}
          <Button variant="inline-link" iconName="add-plus" onClick={addMapping}>
            Add mapping
          </Button>
        </SpaceBetween>
      </div>
    </SpaceBetween>
  );

  const emailTab = (
    <SpaceBetween size="l">
      <div>
        <Header
          variant="h3"
          actions={<Button iconName="refresh" variant="icon" onClick={loadNonAmazonAliases} />}
        >
          Alias → Email Mapping
        </Header>
        <Box variant="p" color="text-body-secondary">
          Map each alias to a delivery email address for daily digest notifications.
        </Box>
      </div>

      {aliasesError ? <Alert type="error">{aliasesError}</Alert> : null}

      {/* Entry cards: one alias with one or more delivery emails. */}
      <SpaceBetween size="s">
        {emailMappings.map((row) => (
          <Box key={row.id} variant="div" padding="s">
            <div style={{ border: "1px solid #e9ebed", borderRadius: 8, padding: 16 }}>
              <SpaceBetween size="xs">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <Input
                      placeholder="Alias (e.g. johndoe or jane@partner.com)"
                      value={row.alias}
                      onChange={({ detail }) => updateEmailAlias(row.id, detail.value)}
                    />
                  </div>
                  <Button
                    variant="icon"
                    iconName="close"
                    ariaLabel="Remove alias mapping"
                    onClick={() => removeEmailCard(row.id)}
                  />
                </div>
                <Box variant="awsui-key-label">Delivery emails:</Box>
                {row.emails.map((email, emailIndex) => (
                  <Input
                    key={emailIndex}
                    placeholder="recipient@example.com"
                    value={email}
                    onChange={({ detail }) => updateEmailAt(row.id, emailIndex, detail.value)}
                  />
                ))}
                <Button variant="inline-link" iconName="add-plus" onClick={() => addEmail(row.id)}>
                  Add delivery email
                </Button>
              </SpaceBetween>
            </div>
          </Box>
        ))}
        <Button variant="inline-link" iconName="add-plus" onClick={addEmailCard}>
          Add alias mapping
        </Button>
      </SpaceBetween>
      <Box float="right">
        <Button
          variant="primary"
          loading={addAliasLoader}
          disabled={!emailMappings.some((m) => (m.alias || "").trim() && m.emails.some((e) => e.trim()))}
          onClick={submitEmailMappings}
        >
          Add mappings
        </Button>
      </Box>

      <Box variant="awsui-key-label">Current mappings</Box>
      <Table
        loading={aliasesLoader}
        loadingText="Loading aliases..."
        items={nonAmazonAliases}
        columnDefinitions={[
          { id: "alias", header: "Alias", cell: (item) => item.alias },
          { id: "email", header: "Email", cell: (item) => item.email },
          { id: "domain", header: "Domain", cell: (item) => item.domain },
          { id: "created_by", header: "Created by", cell: (item) => item.created_by },
          {
            id: "actions",
            header: "Actions",
            cell: (item) => (
              <Button variant="inline-link" iconName="remove" onClick={() => removeAlias(item.alias, item.email)}>
                Delete
              </Button>
            ),
          },
        ]}
        empty={<Box textAlign="center">No non-Amazon aliases</Box>}
        variant="embedded"
      />

      <div
        style={{
          background: "#f0f7ff",
          border: "1px solid #cfe3ff",
          borderRadius: 8,
          padding: 16,
        }}
      >
        <SpaceBetween size="xs">
          <Box variant="strong">How it works</Box>
          <Box variant="small">
            • Amazon aliases (e.g. johndoe) — digest sent to the mapped email instead of the default
            @amazon.com inbox
          </Box>
          <Box variant="small">
            • External aliases (e.g. jane@partner.com) — digest sent to the specified delivery email(s)
          </Box>
          <Box variant="small">
            • An alias can have multiple delivery emails (add several, or comma-separate them) —
            the digest is sent to all of them
          </Box>
          <Box variant="small">
            • The email domain must be an approved subsidiary domain
          </Box>
          <Box variant="small">
            • Actions (upload, approve) are always attributed to the logged-in Amazon alias —
            non-Amazon aliases are notification-only
          </Box>
        </SpaceBetween>
      </div>
    </SpaceBetween>
  );

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Control Panel"
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss} disabled={saving}>
              {/* The Email tab persists inline (add/delete), so there is nothing to
                  batch-save there; offer Close instead of Cancel. */}
              {activeTabId === "email" ? "Close" : "Cancel"}
            </Button>
            {activeTabId !== "email" && (
              <Button
                variant="primary"
                onClick={handleSave}
                loading={saving}
                disabled={
                  loading || saving || (activeTabId === "controls" && workdayDayInvalid)
                }
              >
                {/* Save persists only the active tab (each tab audits independently,
                    so untouched tabs are not rewritten). Name the tab in the button
                    so it is clear edits on other tabs are saved separately. */}
                Save {TAB_LABELS[activeTabId] || ""}
              </Button>
            )}
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error && (
          <Alert type="error" header="Couldn't save settings">
            {error}
          </Alert>
        )}
        {loading ? (
          <Box color="text-body-secondary">Loading settings...</Box>
        ) : (
          <Tabs
            activeTabId={activeTabId}
            onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
            tabs={[
              { id: "controls", label: "Controls", content: controlsTab },
              { id: "mapping", label: "Mapping Configuration", content: mappingTab },
              { id: "email", label: "Email Mapping", content: emailTab },
            ]}
          />
        )}
      </SpaceBetween>
    </Modal>
  );
};

export default ControlPanelModal;
